import { useEffect } from "react"
import { useNavigate, useRouterState } from "@tanstack/react-router"
import { Spin, Result, Layout } from "@mimber/ui-web"
import { useAuth } from "@/core/contexts"
import { AuthGateProps } from "./AuthGate.types"

const { Content } = Layout

export const AuthGate: React.FunctionComponent<AuthGateProps> = ({
  children,
}) => {
  const navigate = useNavigate()
  const { isLoading, isAuthenticated, isError, error, logout } = useAuth()
  const currentPath = useRouterState({
    select: (state) => state.location.pathname + state.location.searchStr,
  })
  const hasChildren = Boolean(children)

  // Redirects must run in an effect, not directly in the render body — doing
  // it in render can re-fire on every re-render while a navigation is still
  // pending (e.g. `currentPath` momentarily reflecting the in-flight `/login`
  // destination), compounding the `return` search param into an ever-growing
  // self-referential URL.
  useEffect(() => {
    if (isLoading || isError) return

    if (isAuthenticated) {
      if (!hasChildren) {
        navigate({ to: "/dashboard" })
      }
      return
    }

    // Never redirect to /login from /login itself, so an in-flight
    // navigation can never wrap its own `return` value into itself again.
    if (currentPath.startsWith("/login")) return

    navigate({ to: "/login", search: { return: currentPath } })
  }, [isLoading, isError, isAuthenticated, hasChildren, currentPath, navigate])

  // Show loading while authentication is in progress
  if (isLoading) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "var(--color-background)",
          }}
        >
          <div className="flex flex-col items-center text-center">
            <Spin size="large" />
            <p className="mt-4 text-muted-foreground">Authenticating...</p>
          </div>
        </Content>
      </Layout>
    )
  }

  // Show authentication error
  if (isError && error) {
    return (
      <Layout style={{ minHeight: "100vh" }}>
        <Content
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "2rem",
            background: "var(--color-background)",
          }}
        >
          <div className="max-w-md w-full">
            <Result
              status="error"
              title="Authentication Failed"
              subTitle={
                error?.message || "An error occurred during authentication"
              }
              extra={[
                <button
                  key="logout"
                  className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
                  onClick={() => logout()}
                >
                  Sign Out
                </button>,
              ]}
            />
          </div>
        </Content>
      </Layout>
    )
  }

  // User is authenticated - render children (redirect to /dashboard, if any,
  // is handled by the effect above)
  if (isAuthenticated) {
    return hasChildren ? <>{children}</> : null
  }

  // User is not authenticated - redirect to login is handled by the effect
  // above
  return null
}
